/**
 * Разбор конфликтов на экране ревью (§6.4, §11.6, §12.5).
 *
 * Конфликт — это не ошибка, а развилка: человеку показывают три значения
 * (что было, что он меняет, что в данных сейчас), автора и время чужой правки,
 * и дают три равноправных выхода. Формулировки §12.5 взяты дословно.
 *
 * Пока хоть один конфликт не решён, публикация заблокирована — объясняет это
 * сама кнопка на экране ревью, а не тост и не красный текст.
 */
import type { Change, Conflict, UserSummary } from '@shared/types.ts'
import { Button, IconWarning } from '../../components/index.js'
import { brandNameOf, changeTitle, labelOrRaw, valueText, type ReviewContext } from './ChangeLine.js'
import { draftTexts, formatMoment } from './texts.draft.js'
import './ConflictResolver.css'

export type ConflictDecision = 'mine' | 'theirs' | 'drop'
export type ConflictDecisions = Record<number, ConflictDecision>

export interface ConflictResolverProps {
  conflicts: Conflict[]
  /** Правки черновика по id: конфликт приходит только с `changeId`. */
  changesById: Map<number, Change>
  ctx: ReviewContext
  users: UserSummary[]
  decisions: ConflictDecisions
  onDecide: (changeId: number, decision: ConflictDecision) => void
  onDecideAll: (decision: 'mine' | 'theirs') => void
}

export function ConflictResolver({
  conflicts, changesById, ctx, users, decisions, onDecide, onDecideAll,
}: ConflictResolverProps) {
  const known = conflicts.filter((conflict) => changesById.has(conflict.changeId))
  if (known.length === 0) return null

  const heading = draftTexts.conflict.heading(known.length)

  return (
    <section className="conflicts" aria-label={heading}>
      <h2 className="conflicts__head t-cell">
        <span className="conflicts__head-icon" aria-hidden="true"><IconWarning /></span>
        {heading}
      </h2>

      {known.map((conflict) => {
        const change = changesById.get(conflict.changeId)
        if (!change) return null

        const title = `${brandNameOf(change, ctx)} · ${changeTitle(change, ctx)}`
        const mine = valueText(change, 'new')
        const theirs = labelOrRaw(change, conflict.currentLabel, conflict.currentValue)
        const author = users.find((user) => user.id === conflict.changedBy)
        const source = draftTexts.conflict.source(
          author?.fullName ?? draftTexts.conflict.unknownAuthor,
          formatMoment(conflict.changedAt),
          conflict.changeSetId,
        )
        const name = `conflict-${conflict.changeId}`
        const decision = decisions[conflict.changeId]

        return (
          <div className="conflicts__item" key={conflict.changeId}>
            <p className="conflicts__title t-cell">{title}</p>

            <dl className="conflicts__values">
              <dt className="t-meta">{draftTexts.conflict.wasAtStart}</dt>
              <dd className="t-cell">{valueText(change, 'old')}</dd>
              <dt className="t-meta">{draftTexts.conflict.youChangeTo}</dt>
              <dd className="t-cell conflicts__mine">{mine}</dd>
              <dt className="t-meta">{draftTexts.conflict.nowInData}</dt>
              <dd className="t-cell">
                <span className="conflicts__theirs">{theirs}</span>
                <span className="conflicts__source t-meta">{` — ${source}`}</span>
              </dd>
            </dl>

            <fieldset className="conflicts__choices">
              <legend className="visually-hidden">{draftTexts.conflict.groupAria(title)}</legend>
              <label className="conflicts__choice">
                <input
                  type="radio"
                  className="conflicts__radio"
                  name={name}
                  checked={decision === 'mine'}
                  onChange={() => onDecide(conflict.changeId, 'mine')}
                />
                <span className="t-cell">{draftTexts.conflict.keepMine(mine)}</span>
              </label>
              <label className="conflicts__choice">
                <input
                  type="radio"
                  className="conflicts__radio"
                  name={name}
                  checked={decision === 'theirs'}
                  onChange={() => onDecide(conflict.changeId, 'theirs')}
                />
                <span className="t-cell">{draftTexts.conflict.takeTheirs(theirs)}</span>
              </label>
              <label className="conflicts__choice">
                <input
                  type="radio"
                  className="conflicts__radio"
                  name={name}
                  checked={decision === 'drop'}
                  onChange={() => onDecide(conflict.changeId, 'drop')}
                />
                <span className="t-cell">{draftTexts.conflict.dropChange}</span>
              </label>
            </fieldset>
          </div>
        )
      })}

      {known.length > 1 ? (
        <div className="conflicts__bulk">
          <Button size="s" variant="secondary" onClick={() => onDecideAll('mine')}>
            {draftTexts.conflict.keepAllMine}
          </Button>
          <Button size="s" variant="secondary" onClick={() => onDecideAll('theirs')}>
            {draftTexts.conflict.takeAllTheirs}
          </Button>
        </div>
      ) : null}
    </section>
  )
}
